/* ==========================================================================
   TASK 260921T1D — type_collection_v2 Compare + Sticky Pin Panel.

   Lightweight comparison: select products from the rendered table, sticky
   tray while scrolling, side-by-side comparison panel.

   TASK 260923T1F-P8.7 - Version B:
     * The Compare control moved to the FRONT of the sticky filter bar as
       "Compare products (N)" and stays there across facet re-renders.
     * The panel now also shows Thickness, Price (with its Price Range band,
       e.g. "$61/m2 | $$$") and the real per-colour swatch images: every unique
       colour, wrapped over as many lines as needed, no "+N" chip, no slider.

   Reads only from already-rendered DOM cells. No AJAX, no duplicate data.
   Storage: localStorage key rf_tcv2_compare_<collection handle>.
   Different from T1C's rf_tcv_columns_ key.

   Selection limits (260923T4):
     <=480px:              max 3
     481-767px:            max 4
     768-1023px (tablet):  max 3  (unchanged)
     >=1024px (desktop):   max 4  (unchanged)
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY_PREFIX = 'rf_tcvc_compare_';
  var TABLE_SEL = '[data-tcv-table]';
  var ROW_SEL = 'tr.rf-tcv__row';
  var BRAND_ROW_SEL = 'tr.rf-tcv__group-row';

  /* ---- viewport-based limits ---- */

  function getMaxSelection() {
    var w = window.innerWidth || document.documentElement.clientWidth;
    if (w <= 480) return 3;
    if (w <= 767) return 4;
    if (w >= 1024) return 4;
    return 3;
  }

  /* ---- state (in-memory, refreshed from storage on init) ---- */

  var state = {
    collection: null,
    selected: [],
    panelOpen: false
  };

  function storageKey() {
    return STORAGE_KEY_PREFIX + (state.collection || 'all');
  }

  function readState() {
    try {
      var raw = window.localStorage.getItem(storageKey());
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(function (product) {
        var table = document.querySelector(TABLE_SEL);
        var priceEnabled = table && table.getAttribute('data-tcv-price-enabled') === 'true';
        if (!priceEnabled) {
          delete product.priceText;
          if (product.specs) delete product.specs.price;
        }
        /* Rows stored before P8.7 carry no swatch payload yet. */
        if (!Array.isArray(product.colours)) product.colours = [];
        return product;
      });
    } catch (e) {
      return [];
    }
  }

  function writeState() {
    try {
      if (state.selected.length) {
        window.localStorage.setItem(storageKey(), JSON.stringify(state.selected));
      } else {
        window.localStorage.removeItem(storageKey());
      }
    } catch (e) {
      /* private mode */
    }
  }

  /* ---- data extraction from rendered rows ---- */

  function extractProductData(row) {
    var link = row.querySelector('.rf-tcv__product-link');
    if (!link) return null;

    var nameEl = row.querySelector('.rf-tcv__name');
    var secondaryEl = row.querySelector('.rf-tcv__secondary');
    var thumbImg = row.querySelector('.rf-tcv__thumb-img');

    var href = link.getAttribute('href') || '';
    // Extract handle from URL like /products/some-handle
    var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
    // Get product ID from href if available, otherwise use a stable fallback
    var productId = handle;

    var specs = {};

    /* Version B rows are one label | value specification grid, so each value
       carries data-tcvb-spec instead of living in its own td[data-col]
       (Version A, unchanged, keeps the per-column cells). */
    var marks = row.querySelectorAll('[data-tcvb-spec]');
    if (marks.length) {
      for (var m = 0; m < marks.length; m++) {
        var key = marks[m].getAttribute('data-tcvb-spec');
        var value = marks[m].textContent.trim();
        if (key && value) specs[key] = value;
      }
    } else {
      var cells = row.querySelectorAll('td[data-col]');
      for (var i = 0; i < cells.length; i++) {
        var col = cells[i].getAttribute('data-col');
        var text = cells[i].textContent.trim();
        if (text) specs[col] = text;
      }
    }

    /* TASK 260923T1F-P8.7 - the real colour swatches for the Compare panel:
       "name|||image_url" pairs joined by ";;;". They are deduplicated by colour
       NAME in the snippet (regardless of variant or plank size) and deduplicated
       again here, so a stale stored entry can never repeat a colour. */
    var colours = [];
    var seenColourNames = [];
    var colourPayload = row.getAttribute('data-tcvb-colours') || '';
    if (colourPayload) {
      colourPayload.split(';;;').forEach(function (entry) {
        var parts = entry.split('|||');
        var colourName = (parts[0] || '').trim();
        var colourSrc = (parts[1] || '').trim();
        var colourKey = colourName.toLowerCase();
        if (!colourSrc || seenColourNames.indexOf(colourKey) !== -1) return;
        seenColourNames.push(colourKey);
        colours.push({ name: colourName, src: colourSrc });
      });
    }

    /* The thickness already lives inside the product title (Option 4 tag); when
       the row carries the value as data instead, the panel still shows it. */
    if (!specs.thickness) {
      var rowThickness = row.getAttribute('data-tcvb-thickness') || '';
      if (rowThickness) specs.thickness = rowThickness;
    }

    var sizeNodes = row.querySelectorAll('[data-tcv-product-size]');
    var sizes = [];
    for (var j = 0; j < sizeNodes.length; j++) {
      var sizeText = sizeNodes[j].textContent.trim();
      if (sizeText) sizes.push(sizeText);
    }
    if (sizes.length) specs.size = sizes.join('\n');

    /* One stable row order in the panel, however the row happened to be built. */
    var order = ['thickness', 'size', 'pattern', 'water', 'ac', 'price_range', 'price'];
    var ordered = {};
    for (var o = 0; o < order.length; o++) {
      if (typeof specs[order[o]] === 'string') ordered[order[o]] = specs[order[o]];
    }
    for (var specKey in specs) {
      if (specs.hasOwnProperty(specKey) && typeof ordered[specKey] !== 'string') {
        ordered[specKey] = specs[specKey];
      }
    }
    specs = ordered;

    return {
      id: productId,
      handle: handle,
      title: nameEl ? nameEl.textContent.trim() : '',
      secondary: secondaryEl ? secondaryEl.textContent.trim() : '',
      imageSrc: thumbImg ? thumbImg.getAttribute('src') : null,
      imageAlt: thumbImg ? thumbImg.getAttribute('alt') : '',
      specs: specs,
      colours: colours
    };
  }

  /* Every unique colour of a stored product, in payload order. */
  function colourList(product) {
    if (!product || !Array.isArray(product.colours)) return [];
    return product.colours.filter(function (colour) {
      return colour && colour.src;
    });
  }

  function findRowById(id) {
    var table = document.querySelector(TABLE_SEL);
    if (!table) return null;
    var rows = table.querySelectorAll(ROW_SEL);
    for (var i = 0; i < rows.length; i++) {
      var link = rows[i].querySelector('.rf-tcv__product-link');
      if (!link) continue;
      var href = link.getAttribute('href') || '';
      var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
      if (handle === id || href.indexOf('/' + id + '?') !== -1 || href.endsWith('/' + id)) {
        return rows[i];
      }
    }
    return null;
  }

  /* ---- Compare checkbox in each product row ---- */

  function addCompareCheckboxes(table) {
    var rows = table.querySelectorAll(ROW_SEL);
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (row.querySelector('[data-tcv-compare-toggle]')) continue; // already added

      var productTh = row.querySelector('.rf-tcv__product');
      if (!productTh) continue;

      var link = productTh.querySelector('.rf-tcv__product-link');
      if (!link) continue;

      var href = link.getAttribute('href') || '';
      var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');

      var checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.setAttribute('data-tcv-compare-toggle', '');
      checkbox.setAttribute('data-tcv-product-id', handle);
      var nameEl = row.querySelector('.rf-tcv__name');
      var ariaLabel = nameEl ? 'Compare ' + nameEl.textContent.trim() : 'Compare this product';
      checkbox.setAttribute('aria-label', ariaLabel);
      checkbox.className = 'rf-tcv__compare-cb';

      var compareCell = document.createElement('td');
      compareCell.className = 'rf-tcvc__compare-cell';
      var hit = document.createElement('label');
      hit.className = 'rf-tcvc__compare-inner';
      hit.appendChild(checkbox);
      compareCell.appendChild(hit);
      row.insertBefore(compareCell, productTh);
    }
  }

  function syncCheckboxes() {
    var selectedIds = state.selected.map(function (p) { return p.id; });
    var cbs = document.querySelectorAll('[data-tcv-compare-toggle]');
    for (var i = 0; i < cbs.length; i++) {
      var id = cbs[i].getAttribute('data-tcv-product-id');
      cbs[i].checked = selectedIds.indexOf(id) !== -1;
    }
  }

  /* ---- compact action in the top controls ---- */

  function ensureTray() {
    var existing = document.querySelector('[data-tcv-compare-tray]');
    if (existing) {
      placeTray(existing);
      return;
    }

    var tray = document.createElement('div');
    tray.setAttribute('data-tcv-compare-tray', '');
    tray.setAttribute('role', 'region');
    tray.setAttribute('aria-label', 'Compare selected products');
    tray.className = 'rf-tcv-compare-tray';

    var compareBtn = document.createElement('button');
    compareBtn.type = 'button';
    compareBtn.className = 'rf-tcv-compare-tray-btn';
    compareBtn.setAttribute('data-tcv-compare-open', '');

    var compareText = document.createElement('span');
    compareText.className = 'rf-tcvc__action-label';
    compareText.textContent = 'Compare';

    var compareCount = document.createElement('span');
    compareCount.className = 'rf-tcvc__action-count';
    compareCount.setAttribute('data-tcv-compare-count', '');
    compareCount.textContent = String(state.selected.length);

    compareBtn.appendChild(compareText);
    compareBtn.appendChild(compareCount);
    compareBtn.disabled = state.selected.length === 0;

    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'rf-tcv-compare-tray-btn rf-tcv-compare-tray-btn--clear';
    clearBtn.setAttribute('data-tcv-compare-clear', '');
    clearBtn.textContent = 'Clear';
    clearBtn.hidden = state.selected.length === 0;

    tray.appendChild(compareBtn);
    tray.appendChild(clearBtn);

    var controls = document.querySelector('.rf-tcv__controls');
    if (controls) controls.appendChild(tray);
    placeTray(tray);
  }

  /* TASK 260923T1F-P8.7 - the Compare control now opens the sticky filter bar:
       Compare products (0) | Filter: Price | Brand | ... | 28 products | Columns
     It is inserted as the FIRST thing in #main-collection-filters (before the
     facet container), so it stays put while the facets re-render inside
     #ProductGridContainer. A layout without a filter bar keeps the old home in
     the toolbar row. */
  function trayHost() {
    return document.querySelector('[data-tcvc-sticky-actions]') ||
      document.querySelector('#main-collection-filters') ||
      document.querySelector('.rf-tcv__controls');
  }

  function placeTray(tray) {
    var host = trayHost();
    if (!host) return;
    if (host.id === 'main-collection-filters') {
      var anchor = host.querySelector('.facets-container') || host.firstChild;
      if (tray.parentNode !== host || tray.nextElementSibling !== (anchor && anchor.nodeType === 1 ? anchor : null)) {
        host.insertBefore(tray, anchor);
      }
      return;
    }
    var filterProxy = host.querySelector('[data-tcvc-filter-proxy]');
    if (filterProxy) {
      if (tray.parentNode !== host || tray.nextElementSibling !== filterProxy) {
        host.insertBefore(tray, filterProxy);
      }
      return;
    }
    if (tray.parentNode !== host) host.appendChild(tray);
  }

  function compareLabel() {
    return 'Compare (' + state.selected.length + ')';
  }

  function updateTray() {
    var tray = document.querySelector('[data-tcv-compare-tray]');
    if (!tray) {
      ensureTray();
      return;
    }

    var compareBtn = tray.querySelector('[data-tcv-compare-open]');
    if (compareBtn) {
      var compareCount = compareBtn.querySelector('[data-tcv-compare-count]');
      if (compareCount) compareCount.textContent = String(state.selected.length);
      compareBtn.disabled = state.selected.length === 0;
    }
    var clearBtn = tray.querySelector('[data-tcv-compare-clear]');
    if (clearBtn) clearBtn.hidden = state.selected.length === 0;
    placeTray(tray);
    document.dispatchEvent(new CustomEvent('rf:tcv-selection-change', {
      detail: { count: state.selected.length }
    }));
  }

  /* ---- compare panel ---- */

  function buildPanel() {
    if (state.panelOpen) return;
    state.panelOpen = true;

    var overlay = document.createElement('div');
    overlay.className = 'rf-tcv-compare-overlay';
    overlay.setAttribute('data-tcv-compare-overlay', '');
    overlay.setAttribute('data-tcv-compare-close-bg', '');

    var panel = document.createElement('div');
    panel.className = 'rf-tcv-compare-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Compare products');
    panel.setAttribute('data-tcv-compare-panel', '');
    panel.setAttribute('aria-modal', 'true');

    // Header
    var header = document.createElement('div');
    header.className = 'rf-tcv-compare-panel-header';

    var title = document.createElement('h2');
    title.className = 'rf-tcv-compare-panel-title';
    title.textContent = 'Compare ' + state.selected.length + ' products';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'rf-tcv-compare-panel-close';
    closeBtn.setAttribute('data-tcv-compare-close', '');
    closeBtn.setAttribute('aria-label', 'Close comparison');
    closeBtn.textContent = '×';

    header.appendChild(title);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Body: comparison table
    var body = document.createElement('div');
    body.className = 'rf-tcv-compare-panel-body';

    // Collect all spec keys that have at least one value across selected products
    var allSpecKeys = [];
    var specLabels = {
      'thickness': 'Thickness',
      'size': 'Board Size',
      'pattern': 'Pattern',
      'colours': 'Colours',
      'water': 'Water Resistance',
      'ac': 'AC Rating',
      'price_range': 'Price Range',
      'price': 'Price'
    };

    for (var i = 0; i < state.selected.length; i++) {
      var specs = state.selected[i].specs;
      for (var key in specs) {
        if (specs.hasOwnProperty(key) && allSpecKeys.indexOf(key) === -1) {
          allSpecKeys.push(key);
        }
      }
    }

    // Build comparison table
    var compTable = document.createElement('table');
    compTable.className = 'rf-tcv-compare-table';

    // Header row with product names
    var thead = document.createElement('thead');
    var headerRow = document.createElement('tr');

    var thLabel = document.createElement('th');
    thLabel.className = 'rf-tcv-compare-label-col';
    thLabel.textContent = '';
    headerRow.appendChild(thLabel);

    for (var i = 0; i < state.selected.length; i++) {
      var product = state.selected[i];
      var th = document.createElement('th');
      th.className = 'rf-tcv-compare-product-header';

      if (product.imageSrc) {
        var img = document.createElement('img');
        img.src = product.imageSrc;
        img.alt = product.imageAlt || product.title;
        img.className = 'rf-tcv-compare-product-img';
        th.appendChild(img);
      }

      var nameSpan = document.createElement('div');
      nameSpan.className = 'rf-tcv-compare-product-name';
      nameSpan.textContent = product.title;
      th.appendChild(nameSpan);

      if (product.secondary) {
        var secSpan = document.createElement('div');
        secSpan.className = 'rf-tcv-compare-product-secondary';
        secSpan.textContent = product.secondary;
        th.appendChild(secSpan);
      }

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'rf-tcv-compare-product-remove';
      removeBtn.setAttribute('data-tcv-tray-remove', product.id);
      removeBtn.setAttribute('aria-label', 'Remove ' + product.title + ' from compare');
      removeBtn.textContent = 'Remove';
      th.appendChild(removeBtn);

      headerRow.appendChild(th);
    }

    thead.appendChild(headerRow);
    compTable.appendChild(thead);

    // Body rows
    var tbody = document.createElement('tbody');

    // Spec rows
    for (var i = 0; i < allSpecKeys.length; i++) {
      var specKey = allSpecKeys[i];
      var specRow = document.createElement('tr');

      var specLabel = document.createElement('td');
      specLabel.className = 'rf-tcv-compare-row-label';
      specLabel.textContent = specLabels[specKey] || specKey;
      specRow.appendChild(specLabel);

      for (var j = 0; j < state.selected.length; j++) {
        var cell = document.createElement('td');
        cell.className = 'rf-tcv-compare-cell';
        var val = state.selected[j].specs[specKey] || '';
        cell.textContent = val;
        specRow.appendChild(cell);
      }

      tbody.appendChild(specRow);
    }

    /* TASK 260923T1F-P8.7 - Colours row: the real swatch image of EVERY unique
       colour, wrapping over as many lines as the panel needs. No "+N" chip and
       no slider inside the Compare panel. */
    var colourCount = 0;
    for (var c = 0; c < state.selected.length; c++) {
      colourCount = Math.max(colourCount, colourList(state.selected[c]).length);
    }
    if (colourCount > 0) {
      var colourRow = document.createElement('tr');
      var colourLabel = document.createElement('td');
      colourLabel.className = 'rf-tcv-compare-row-label';
      colourLabel.textContent = 'Colours';
      colourRow.appendChild(colourLabel);

      for (var p = 0; p < state.selected.length; p++) {
        var colourCell = document.createElement('td');
        colourCell.className = 'rf-tcv-compare-cell rf-tcv-compare-cell--colours';
        var swatchWrap = document.createElement('div');
        swatchWrap.className = 'rf-tcv-compare-colours';
        var swatchList = colourList(state.selected[p]);
        for (var s = 0; s < swatchList.length; s++) {
          var swatch = document.createElement('img');
          swatch.className = 'rf-tcv-compare-colour';
          swatch.src = swatchList[s].src;
          swatch.alt = swatchList[s].name || 'Colour';
          swatch.title = swatchList[s].name || '';
          swatch.loading = 'lazy';
          swatchWrap.appendChild(swatch);
        }
        colourCell.appendChild(swatchWrap);
        colourRow.appendChild(colourCell);
      }

      tbody.appendChild(colourRow);
    }

    compTable.appendChild(tbody);
    body.appendChild(compTable);
    panel.appendChild(body);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Focus management
    closeBtn.focus();

    // Trap focus
    document.addEventListener('keydown', panelKeyHandler);
  }

  function panelKeyHandler(e) {
    if (e.key === 'Escape') {
      closePanel();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      var panel = document.querySelector('[data-tcv-compare-panel]');
      if (!panel) return;
      var focusable = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function closePanel() {
    state.panelOpen = false;
    var overlay = document.querySelector('[data-tcv-compare-overlay]');
    if (overlay) {
      overlay.parentNode.removeChild(overlay);
    }
    document.removeEventListener('keydown', panelKeyHandler);

    // Return focus to the Compare button in the tray
    var trayBtn = document.querySelector('[data-tcv-compare-open]');
    if (trayBtn) trayBtn.focus();
  }

  /* ---- selection logic ---- */

  function toggleProduct(id, addToSelection, sourceCheckbox) {
    var existingIndex = -1;
    for (var i = 0; i < state.selected.length; i++) {
      if (state.selected[i].id === id) {
        existingIndex = i;
        break;
      }
    }

    if (addToSelection) {
      if (existingIndex !== -1) return; // already selected

      var max = getMaxSelection();
      if (state.selected.length >= max) {
        // 260923T4 — keep the current selection: drop the extra tick, then report the active limit
        syncCheckboxes();
        showLimitFeedback(max);
        return;
      }

      // Extract data from the row
      var row = findRowById(id);
      if (!row) return;

      var data = extractProductData(row);
      if (!data) return;

      state.selected.push(data);
      writeState();
      syncCheckboxes();
      updateTray();

      // If panel is open, rebuild it to include new product
      if (state.panelOpen) {
        var overlay = document.querySelector('[data-tcv-compare-overlay]');
        if (overlay) overlay.parentNode.removeChild(overlay);
        state.panelOpen = false;
        buildPanel();
      }
    } else {
      if (existingIndex === -1) return;

      var openOverlay = document.querySelector('[data-tcv-compare-overlay]');
      var wasPanelOpen = !!openOverlay || state.panelOpen;

      if (openOverlay && openOverlay.parentNode) {
        openOverlay.parentNode.removeChild(openOverlay);
      }
      if (wasPanelOpen) {
        state.panelOpen = false;
        document.removeEventListener('keydown', panelKeyHandler);
      }

      state.selected.splice(existingIndex, 1);
      writeState();
      syncCheckboxes();
      updateTray();

      if (wasPanelOpen && state.selected.length > 0) {
        buildPanel();
      }
    }
  }

  function clearAll() {
    state.selected = [];
    writeState();
    syncCheckboxes();
    updateTray();
  }

  /* ---- feedback toast ---- */

  var toastTimeout = null;

  function showLimitFeedback(max) {
    var existing = document.querySelector('[data-tcv-compare-toast]');
    if (existing) return; // already showing

    var toast = document.createElement('div');
    toast.className = 'rf-tcv-compare-toast';
    toast.setAttribute('data-tcv-compare-toast', '');
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = 'You can compare up to ' + max + ' products.';

    document.body.appendChild(toast);

    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 3000);
  }

  /* ---- event handling (delegated) ---- */

  function initEvents() {
    // Compare checkbox toggles
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var cb = target.closest('[data-tcv-compare-toggle]');
      if (!cb) return;

      var id = cb.getAttribute('data-tcv-product-id');
      toggleProduct(id, cb.checked);
    });

    // Delegated clicks for tray, panel, etc.
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      // Open compare panel
      if (target.closest('[data-tcv-compare-open]')) {
        buildPanel();
        return;
      }

      // Close compare panel (button)
      if (target.closest('[data-tcv-compare-close]')) {
        closePanel();
        return;
      }

      // Close compare panel (overlay background click)
      if (target.hasAttribute && target.hasAttribute('data-tcv-compare-close-bg')) {
        closePanel();
        return;
      }

      // Clear all
      if (target.closest('[data-tcv-compare-clear]')) {
        clearAll();
        return;
      }

      // Remove individual product from tray
      var removeBtn = target.closest('[data-tcv-tray-remove]');
      if (removeBtn) {
        var id = removeBtn.getAttribute('data-tcv-tray-remove');
        toggleProduct(id, false);
        return;
      }
    });
  }

  /* ---- facets re-render handling ---- */

  function handleFacetsReRender() {
    var table = document.querySelector(TABLE_SEL);
    if (table) {
      addCompareCheckboxes(table);
      syncCheckboxes();

      // Verify selected products still exist in the current view
      var selectedIds = state.selected.map(function (p) { return p.id; });
      var visibleIds = [];
      var rows = table.querySelectorAll(ROW_SEL);
      for (var i = 0; i < rows.length; i++) {
        var link = rows[i].querySelector('.rf-tcv__product-link');
        if (link) {
          var href = link.getAttribute('href') || '';
          var handle = href.replace(/^\/products\//, '').replace(/\?.*$/, '');
          visibleIds.push(handle);
        }
      }

      // Remove products that are no longer visible (filtered out)
      var toRemove = [];
      for (var i = 0; i < state.selected.length; i++) {
        if (visibleIds.indexOf(state.selected[i].id) === -1) {
          toRemove.push(state.selected[i].id);
        }
      }

      if (toRemove.length > 0) {
        state.selected = state.selected.filter(function (p) {
          return toRemove.indexOf(p.id) === -1;
        });
        writeState();
        syncCheckboxes();
        updateTray();
      }
    }
    window.setTimeout(ensureTray, 200);
  }

  /* ---- init ---- */

  function init() {
    var table = document.querySelector(TABLE_SEL);
    if (!table) return;

    // Determine collection handle
    var scope = table.closest('.rf-tcv');
    if (scope) {
      var control = scope.querySelector('[data-tcv-columns-control]');
      if (control) {
        state.collection = control.getAttribute('data-tcv-collection') || 'all';
      }
    }
    if (!state.collection) state.collection = 'all';

    // Load persisted state
    state.selected = readState();

    // Add compare checkboxes
    addCompareCheckboxes(table);
    syncCheckboxes();

    ensureTray();

    window.addEventListener('resize', function () {
      var tray = document.querySelector('[data-tcv-compare-tray]');
      if (tray) placeTray(tray);
    });

    // Event handling
    initEvents();

    // Listen for facet re-renders (the existing columns.js uses MutationObserver)
    if (typeof window.MutationObserver === 'function') {
      var gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) {
        new window.MutationObserver(function (mutations) {
          handleFacetsReRender();
        }).observe(gridContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
